import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { authErrorKey } from "@/lib/auth-error";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Giriş — JUËN" },
      { name: "description", content: "JUËN üyeliğinize giriş yapın." },
    ],
  }),
  component: LoginPage,
});

const EMAIL_STORAGE_KEY = "juen.rememberEmail";
const LEGACY_STORAGE_KEY = "juen.rememberMe";

function LoginPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      // Clean up any legacy entry that may still hold a plaintext password.
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
      if (savedEmail) {
        setEmail(savedEmail);
        setRemember(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // If the user typed a plain username (no @), treat it as a waiter login
    const loginEmail = email.includes("@")
      ? email
      : `${email.trim().toLowerCase()}@waiters.juen.local`;
    const { data: signInData, error } = await supabase.auth.signInWithPassword({ email: loginEmail, password });
    setLoading(false);
    if (error) {
      toast.error(t(authErrorKey(error.message)));
      return;
    }
    try {
      if (remember) {
        localStorage.setItem(EMAIL_STORAGE_KEY, email);
      } else {
        localStorage.removeItem(EMAIL_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
    toast.success(t("login.welcome"));

    // Waiter-only accounts go directly to the scanner
    let dest: "/harikaradmin/scan" | "/harikaradmin" = "/harikaradmin";
    const uid = signInData.user?.id;
    if (uid) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid);
      const roleSet = new Set((roles ?? []).map((r) => r.role));
      if (roleSet.has("waiter") && !roleSet.has("admin")) {
        dest = "/harikaradmin/scan";
      }
    }
    void navigate({ to: dest });
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-md px-4 py-16">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight">{t("login.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("login.subtitle")}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8"
        >
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.emailOrUser")}</Label>
            <Input id="email" type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("login.password")}</Label>
            <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={remember}
              onCheckedChange={(v) => setRemember(v === true)}
            />
            <Label htmlFor="remember" className="cursor-pointer text-sm font-normal">
              {t("login.remember")}
            </Label>
          </div>

          <Button type="submit" disabled={loading} className="h-12 w-full text-base">
            {loading ? t("login.submitting") : t("login.submit")}
          </Button>

        </form>
      </div>
    </div>
  );
}
